import { message } from "antd";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { axios, getToken, URL_API } from "../../utils";
import { resCatch } from "../../views/util/helper";
import {
  CARD_HEIGHT,
  CARD_WIDTH,
  DEFAULT_CONTACT_STATUS,
  MINIMIZED_HEIGHT,
  MINIMIZED_WIDTH,
  REASON_REQUIRED_RESULTS,
} from "./constants";

const getHeaders = () => ({ Authorization: "Bearer " + getToken() });

// :: ---------------- API Helpers ---------------- ::

const getConversationNoteApi = async (partnerCode, page = 1, limit = 10) => {
  const response = await axios({
    url: `${URL_API}/crm/conversation-note`,
    method: "get",
    params: { partner_code: partnerCode, page, limit },
    headers: getHeaders(),
  });

  const resData = response.data?.data;
  if (Array.isArray(resData)) {
    return {
      record: resData,
      total: resData.length,
      page: 1,
      limit: resData.length || 10,
      total_pages: 1,
    };
  }

  return {
    record: resData?.record || [],
    total: resData?.total || 0,
    page: resData?.page || page,
    limit: resData?.limit || limit,
    total_pages: resData?.total_pages || 1,
  };
};

const createConversationNoteApi = async (payload) => {
  const response = await axios({
    url: `${URL_API}/crm/conversation-note`,
    headers: getHeaders(),
    method: "post",
    data: payload,
  });
  return response.data?.data;
};

const updateAppointmentStatusApi = async (id, payload) => {
  const response = await axios({
    url: `${URL_API}/crm/conversation-note/${id}/status`,
    headers: getHeaders(),
    method: "post",
    data: payload,
  });
  return response.data?.data;
};

// :: ---------------- useMinimizeNote Hook ---------------- ::

export const useMinimizeNote = ({
  visible,
  partnerCode,
  partnerType = "MEMBER",
  onCancel,
  onSave,
  onDirtyChange,
}) => {
  // Draggable position states
  const [position, setPosition] = useState({ x: 300, y: 150 });
  const [isDragging, setIsDragging] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const elementStart = useRef({ x: 0, y: 0 });
  const hasDraggedRef = useRef(false);

  // Form states
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [contactStatus, setContactStatus] = useState(DEFAULT_CONTACT_STATUS);
  const [reason, setReason] = useState(undefined);
  const [hasPurchase, setHasPurchase] = useState(false);
  const [hasNextAppt, setHasNextAppt] = useState(false);
  const [apptDate, setApptDate] = useState(null);
  const [apptNote, setApptNote] = useState("");

  const isFormDirty =
    Boolean(content && content.trim() !== "") ||
    contactStatus !== DEFAULT_CONTACT_STATUS ||
    reason !== undefined ||
    hasPurchase ||
    hasNextAppt ||
    apptDate !== null ||
    Boolean(apptNote && apptNote.trim() !== "");

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(visible ? isFormDirty : false);
    }
  }, [visible, isFormDirty, onDirtyChange]);

  // Tabs, loading, history list & pagination states
  const [activeTab, setActiveTab] = useState("1");
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const resetForm = () => {
    setTitle("");
    setContent("");
    setContactStatus(DEFAULT_CONTACT_STATUS);
    setReason(undefined);
    setHasPurchase(false);
    setHasNextAppt(false);
    setApptDate(null);
    setApptNote("");
  };

  const loadNotes = async (targetPage = page) => {
    setIsLoading(true);
    try {
      const res = await getConversationNoteApi(partnerCode, targetPage, limit);
      setNotes(res.record);
      setTotal(res.total);
      setPage(res.page);
      setTotalPages(res.total_pages);
    } catch (error) {
      resCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      loadNotes();
    } else {
      setIsMinimized(false);
    }
  }, [visible, partnerCode]);

  const handleMinimize = () => setIsMinimized(true);

  const handleExpand = () => {
    setIsMinimized(false);
    setPosition((prev) => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      return {
        x: Math.min(prev.x, screenWidth - CARD_WIDTH),
        y: Math.min(prev.y, screenHeight - CARD_HEIGHT),
      };
    });
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;

    const tag = e.target.tagName;
    if (
      tag === "INPUT" ||
      tag === "TEXTAREA" ||
      e.target.closest("button") ||
      e.target.closest(".ant-calendar-picker") ||
      e.target.closest(".ant-select") ||
      e.target.closest(".ant-tabs-nav") ||
      e.target.closest(".minimize-note-action-btn")
    ) {
      return;
    }

    setIsDragging(true);
    hasDraggedRef.current = false;
    dragStart.current = { x: e.clientX, y: e.clientY };
    elementStart.current = { x: position.x, y: position.y };
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const deltaX = e.clientX - dragStart.current.x;
      const deltaY = e.clientY - dragStart.current.y;

      if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) {
        hasDraggedRef.current = true;
      }

      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const currentWidth = isMinimized ? MINIMIZED_WIDTH : CARD_WIDTH;
      const currentHeight = isMinimized ? MINIMIZED_HEIGHT : CARD_HEIGHT;

      setPosition({
        x: Math.max(0, Math.min(elementStart.current.x + deltaX, screenWidth - currentWidth)),
        y: Math.max(0, Math.min(elementStart.current.y + deltaY, screenHeight - currentHeight)),
      });
    };

    const handleMouseUp = () => setIsDragging(false);

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, isMinimized]);

  const handleSave = async () => {
    const isReasonRequired = REASON_REQUIRED_RESULTS.has(contactStatus);

    if (!content.trim()) return message.error("กรุณากรอกเนื้อหา");
    if (!contactStatus) return message.error("กรุณาเลือกผลการติดต่อ");
    if (isReasonRequired && !reason) return message.error("กรุณาเลือกสาเหตุ");
    if (hasNextAppt && !apptDate) return message.error("กรุณาเลือกวันที่และเวลานัดหมาย");

    const payload = {
      partner_type: partnerType,
      partner_code: partnerCode,
      description: content,
      contact_result: contactStatus,
      contact_reason: isReasonRequired ? reason : null,
      is_purchased: hasPurchase,
      is_appointment: hasNextAppt,
      appointment_date: apptDate ? apptDate.format("YYYY-MM-DD HH:mm") : null,
      appointment_note: hasNextAppt ? apptNote : "",
    };

    setIsLoading(true);
    try {
      const newNote = await createConversationNoteApi(payload);
      setNotes((prev) => [newNote, ...prev]);
      message.success("บันทึกการติดต่อสำเร็จ");
      setActiveTab("2");
      resetForm();
      if (onSave) onSave(payload);
    } catch (error) {
      resCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditAppointment = async (id, apptDate, apptNote, extraFields = {}) => {
    setIsLoading(true);
    try {
      const formattedDate = apptDate
        ? typeof apptDate === "string"
          ? apptDate
          : apptDate.format("YYYY-MM-DD HH:mm")
        : null;

      const updatedFields = {
        is_appointment: Boolean(formattedDate),
        appointment_date: formattedDate,
        appointment_note: apptNote || "",
        update_date: moment().format("YYYY-MM-DD HH:mm"),
        ...extraFields,
      };

      await updateAppointmentStatusApi(id, updatedFields);
      setNotes((prev) =>
        prev.map((note) => (note.id === id ? { ...note, ...updatedFields } : note))
      );
      message.success("อัปเดตสถานะนัดหมายสำเร็จ");
    } catch (error) {
      resCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  const disabledDate = (current) => current && current < moment().startOf("day");

  return {
    // Draggable position & minimize
    position,
    handleMouseDown,
    isDragging,
    isMinimized,
    handleMinimize,
    handleExpand,
    hasDraggedRef,

    // Form inputs
    title,
    setTitle,
    content,
    setContent,
    contactStatus,
    setContactStatus,
    reason,
    setReason,
    hasPurchase,
    setHasPurchase,
    hasNextAppt,
    setHasNextAppt,
    apptDate,
    setApptDate,
    apptNote,
    setApptNote,

    // Tabs
    activeTab,
    setActiveTab,

    // Loading
    isLoading,

    // Notes history & pagination
    notes,
    page,
    setPage,
    limit,
    setLimit,
    total,
    totalPages,
    loadNotes,
    handleSave,
    handleEditAppointment,
    disabledDate,

    isSaveDisabled:
      !content.trim() ||
      !contactStatus ||
      (REASON_REQUIRED_RESULTS.has(contactStatus) && !reason) ||
      (hasNextAppt && !apptDate),
  };
};
